import { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  useCreateConnection,
  useEditConnection,
} from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { WorkspaceWithSettings } from "src/api/types";
import { Divider } from "src/base-components/Divider";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { DataRetentionFields } from "src/connections/config/DataRetentionFields";
import { EnableNonProdConfigsField } from "src/connections/config/EnableNonProdConfigsField";
import { InboundWebhookConnectionEnvironmentConfig } from "src/connections/config/inboundWebhook/InboundWebhookConnectionEnvironmentConfig";
import { InboundWebhookResourceConfigFieldset } from "src/connections/config/inboundWebhook/InboundWebhookResourceConfigFieldset";
import {
  InboundWebhookURLField,
  WebhookURLs,
} from "src/connections/config/inboundWebhook/InboundWebhookURLField";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { EnvironmentTabs } from "src/connections/config/shared/EnvironmentTabs";
import { errorProps } from "src/connections/config/shared/errorProps";
import { TestInvocationToggle } from "src/connections/config/shared/TestInvocationToggle";
import { UploadIcon } from "src/connections/config/UploadIcon";
import {
  convertBEConnectionToInboundWebhookConnectionConfigInputs,
  convertInboundWebhookConnectionConfigInputsToBEConnection,
  getInboundWebhookConnectionInputsDefaultValues,
  getInboundWebhookResourceConfigDefaultValues,
} from "src/connections/model/inboundWebhook";
import {
  ConnectionCreationFieldError,
  InboundWebhookConnectionConfigInputs,
  InboundWebhookValidationError,
  InboundWebhookValidationErrorResponse,
} from "src/connections/types";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

type InboundWebhookConnectionConfigFormProps = {
  onClose: (webhookURLs: WebhookURLs | null) => void;
  workspace: WorkspaceWithSettings;
  connection?: Connection;
};

/**
 * Removes the `__root__` part of the loc field from the Connect API error
 * when it's returned as part of the error location.
 *
 * Required because of the issue in FastAPI patch we have for handling
 * V1 and V2 pydantic models in the same API.
 *
 * @param loc location array or null from the Connect API error
 * @returns cleaned location field
 */
export const getCompatLoc = (loc: (string | number)[]): (string | number)[] => {
  if (loc.length < 2) return loc;

  if (typeof loc[1] === "string" && loc[1] === "__root__") {
    // remove the `__root__` part of the loc
    // b/c of the issue in FastAPI that adds the
    // `__root__` to the loc
    return loc.filter((_, i) => i !== 1);
  }
  return loc;
};

export const InboundWebhookConnectionConfigForm: React.FC<
  InboundWebhookConnectionConfigFormProps
> = ({ connection, workspace, onClose }) => {
  let defaultValues: Partial<InboundWebhookConnectionConfigInputs>;
  if (connection) {
    defaultValues =
      convertBEConnectionToInboundWebhookConnectionConfigInputs(connection);
  } else {
    defaultValues = getInboundWebhookConnectionInputsDefaultValues();
  }

  const [activationFieldErrors, setActivationFieldErrors] = useState<
    Record<number, ConnectionCreationFieldError[]>
  >({});

  const formMethods = useForm<InboundWebhookConnectionConfigInputs>({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting: formIsSubmitting },
    watch,
    getValues,
    setValue,
    setError,
    control,
  } = formMethods;

  const resourceConfigs = watch("resourceConfigs");
  const enableNonProdConfigs = watch("enableNonProdConfigs");
  const hasWebhookIcon = Boolean(watch("mediaKey"));

  const productionWebhookURL = getValues("productionConfig.webhookURL");
  const sandboxWebhookURL = getValues("sandboxConfig.webhookURL");
  const handleResourceRemoval = (resourceIdToRemove: string) => {
    setValue(
      "resourceConfigs",
      resourceConfigs.filter((resource) => resource.id !== resourceIdToRemove),
    );
  };

  const handleAddResource = () => {
    const newResource = getInboundWebhookResourceConfigDefaultValues();
    setValue("resourceConfigs", [...resourceConfigs, newResource]);
  };

  const createConnectionMutation = useCreateConnection(workspace.base_url);
  const editConnectionMutation = useEditConnection(
    workspace.base_url,
    connection?.id,
  );

  const filterResourceConfigErrors = (
    validationErrors: InboundWebhookValidationError[],
    resourceIndex: number,
  ) => {
    return validationErrors.filter((error) => {
      const loc = getCompatLoc(error.loc);
      // Check if the error location indicates a resource config error
      return (
        loc.length > 3 &&
        loc[1] === "resource_configs" &&
        loc[2] === resourceIndex
      );
    });
  };

  const findResourceFieldError = (
    resourceFieldErrors: InboundWebhookValidationError[],
    fieldName: string,
  ) => {
    const pathSegments = fieldName.split(".");
    return resourceFieldErrors.find((error) => {
      const loc = getCompatLoc(error.loc);
      const locComponents = loc.slice(3); // Get relevant parts of 'loc'
      if (locComponents.length < pathSegments.length) {
        return false;
      }
      // Check if the error location contains the requested field name
      return pathSegments.every(
        (field, index) => locComponents[index] === field,
      );
    });
  };

  const handleErrors = (
    err: unknown,
    resourceConfigs: InboundWebhookConnectionConfigInputs["resourceConfigs"],
  ) => {
    if (!isAxiosError(err) || err.response?.status !== 422) {
      return;
    }

    const castedError = err.response
      ?.data as InboundWebhookValidationErrorResponse;
    if (!castedError.detail) {
      return;
    }

    setError("root.serverError", {
      type: "server",
      message:
        "Webhook creation failed. Please check that all fields are correct.",
    });

    const beToFeMapping: Record<
      string,
      keyof InboundWebhookConnectionConfigInputs["resourceConfigs"][number]
    > = {
      name: "name",
      "configuration.correlation_id_path": "correlationIDPath",
      "configuration.activation": "activation",
    };

    const parsedActivationFieldErrors: Record<
      number,
      ConnectionCreationFieldError[]
    > = {};

    resourceConfigs.forEach((_, index) => {
      const resourceConfigErrors = filterResourceConfigErrors(
        castedError.detail || [],
        index,
      );

      Object.entries(beToFeMapping).forEach(([beField, feField]) => {
        const validationError = findResourceFieldError(
          resourceConfigErrors,
          beField,
        );
        if (validationError) {
          setError(`resourceConfigs.${index}.${feField}`, {
            type: "server",
            message: validationError.msg,
          });
        }
      });

      const activationValidationError = findResourceFieldError(
        resourceConfigErrors,
        "configuration.activation",
      );

      parsedActivationFieldErrors[index] =
        activationValidationError?.ctx?.field_errors || [];
    });

    setActivationFieldErrors(parsedActivationFieldErrors);
  };

  const onSubmit = async (data: InboundWebhookConnectionConfigInputs) => {
    setActivationFieldErrors({});

    const payload =
      convertInboundWebhookConnectionConfigInputsToBEConnection(data);

    try {
      let upsertedConnection: Connection;

      if (connection) {
        upsertedConnection = await editConnectionMutation.mutateAsync({
          id: connection.id,
          payload,
        });
      } else {
        upsertedConnection =
          await createConnectionMutation.mutateAsync(payload);
      }

      const action = connection ? "updated" : "created";
      toastActions.success({
        title: `Connection ${action}`,
        description: `Connection ${data.name} has been ${action}.`,
      });

      // We need to display the webhook URLs for newly created connections
      const productionWebhookURL =
        upsertedConnection.configuration.webhook_url || "";
      const sandboxWebhookURL =
        upsertedConnection.non_prod_env_configs?.["sandbox"]?.webhook_url ||
        null;
      onClose(
        !connection
          ? {
              productionWebhookURL,
              sandboxWebhookURL,
            }
          : null,
      );
    } catch (err) {
      toastActions.failure({
        title: "Error",
        description: "We couldn't save your connection",
      });
      handleErrors(err, data.resourceConfigs);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        data-loc="webhook-connection-config-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Modal.Content>
          {errors.root?.serverError && (
            <div className="mb-6">
              <Callout variant="error">
                {errors.root.serverError.message}
              </Callout>
            </div>
          )}
          {connection && productionWebhookURL && (
            <div className="mb-6 border-gray-200 border-b pb-6">
              <InboundWebhookURLField
                productionWebhookURL={productionWebhookURL}
                // Making sure the second URL is hidden when environments
                // are disabled
                sandboxWebhookURL={
                  enableNonProdConfigs ? sandboxWebhookURL : null
                }
              />
            </div>
          )}

          <div className="mb-6 flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Tooltip
                body="Use a square image under 100 KB in size. Supported formats are SVG, PNG, JPG, and GIF."
                disabled={hasWebhookIcon}
                placement="top"
                title="Upload logo"
              >
                <UploadIcon
                  mediaKey={getValues("mediaKey")}
                  onFileRemoved={() => {
                    setValue("mediaKey", null);
                  }}
                  onUploadFinished={(success, mediaKey) => {
                    if (success && mediaKey) {
                      setValue("mediaKey", mediaKey);
                    }
                  }}
                />
              </Tooltip>
            </div>
            <div className="flex-grow">
              <FormItem key="name" gap="sm" label="Webhook name" isRequired>
                {Boolean(errors.name) && (
                  <ErrorHint>{errors.name?.message}</ErrorHint>
                )}
                <Input
                  data-loc="webhook-connection-name"
                  {...errorProps(isFieldErrored(errors, "name"))}
                  {...register("name", {
                    required: "Webhook name is required",
                    validate: validateNotStartsWithInternalPrefix(
                      `Webhook name cannot start with ${taktileInternalPrefix}`,
                    ),
                  })}
                  placeholder="Enter Webhook Name"
                  fullWidth
                />
              </FormItem>
            </div>
          </div>
          {isFeatureFlagEnabled(FEATURE_FLAGS.powertools) && (
            <FormItem
              key="customSuccessStatusCode"
              gap="sm"
              label="Custom success status code"
            >
              <Controller
                control={control}
                name="customSuccessStatusCode"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={[
                      {
                        key: "200",
                        value: 200,
                      },
                      {
                        key: "202",
                        value: 202,
                      },
                    ]}
                    placement="bottom"
                    value={String(value)}
                    onChange={onChange}
                  />
                )}
              />
            </FormItem>
          )}
          <EnableNonProdConfigsField<InboundWebhookConnectionConfigInputs>
            dataLoc="webhook-connection-env-aware-switch"
            subtitle="This allows you to set up a separate webhook endpoint for testing in sandbox"
            title="Use separate credentials for sandbox Inbound Webhook requests"
          />
          {enableNonProdConfigs ? (
            <>
              <EnvironmentTabs
                prodContent={
                  <InboundWebhookConnectionEnvironmentConfig
                    connection={connection}
                    environment="production"
                    labelsPrefix="Production"
                  />
                }
                prodError={Boolean(errors.productionConfig)}
                sandboxContent={
                  <InboundWebhookConnectionEnvironmentConfig
                    connection={connection}
                    environment="sandbox"
                    labelsPrefix="Sandbox"
                  />
                }
                sandboxError={Boolean(errors.sandboxConfig)}
              />
              <Divider spacing="my-6" />
            </>
          ) : (
            <InboundWebhookConnectionEnvironmentConfig
              connection={connection}
              environment="production"
              labelsPrefix=""
            />
          )}
          {resourceConfigs.map((resourceConfig, i) => {
            return (
              <InboundWebhookResourceConfigFieldset
                key={resourceConfig.id}
                activationFieldErrors={activationFieldErrors?.[i] || []}
                canBeDeleted={resourceConfigs.length > 1}
                handleResourceRemoval={handleResourceRemoval}
                id={resourceConfig.id}
                index={i}
              />
            );
          })}
          <div>
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={handleAddResource}
            >
              Add resource
            </Button>
          </div>

          <div className="mt-6 mb-8">
            <TestInvocationToggle<InboundWebhookConnectionConfigInputs>
              control={control}
              name="productionConfig.allowTestInvocations"
              enableNonProdConfigs={enableNonProdConfigs}
              type="webhook"
            />
          </div>
          <Divider spacing="my-6" />
          <DataRetentionFields workspace={workspace} />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="webhook-connection-save"
              isDisabled={formIsSubmitting}
              htmlType="submit"
              isLoading={formIsSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </FormProvider>
  );
};
